import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import type { PafContradiction, PafContradictionSide } from '@/services/types';

/**
 * One potential fiscal inconsistency, side by side.
 *
 * A sibling of `ContradictionCard` rather than a reuse of it, because the two
 * objects genuinely differ: a fiscal side carries a CASE number linking to the
 * fiscal track, a disputed credit rather than a fine, and a reference date that
 * is raw text rather than a parsed date. Threading six field renames and a link
 * prefix through one component would make both harder to read than either.
 *
 * The product rules it enforces are the same ones:
 *
 * 1. An UNVERIFIED evidence span is never rendered as a citation. The model
 *    quoted it, but it was not found verbatim in the source it was given.
 * 2. The verdict carries the model and prompt version that produced it.
 * 3. The language is "potential inconsistency" throughout.
 * 4. Money is labelled a disputed CREDIT everywhere it appears, because a reader
 *    arriving from the sanction screens will otherwise read it as a fine.
 */
export function PafContradictionCard({ item }: { item: PafContradiction }) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <article className="space-y-3 rounded-lg border bg-card p-4">
      <header className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
            {t('anatel:consistency.potentialInconsistency')}
          </span>
          {item.explanation_category ? (
            <span className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
              {t(`anatel:consistency.categories.${item.explanation_category}`, {
                defaultValue: item.explanation_category,
              })}
            </span>
          ) : null}
          {item.confidence !== null ? (
            <span className="text-xs text-muted-foreground">
              {t('anatel:consistency.confidence', { value: percent.format(item.confidence) })}
            </span>
          ) : null}
          {!item.fully_verified ? (
            <span className="inline-flex items-center gap-1 rounded border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {t('anatel:consistency.unverifiedBadge')}
            </span>
          ) : null}
        </div>
        {item.shared_issue ? (
          <h3 className="text-sm font-semibold text-foreground">{item.shared_issue}</h3>
        ) : null}
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <PafSide side={item.side_a} label={t('anatel:consistency.sideA')} />
        <PafSide side={item.side_b} label={t('anatel:consistency.sideB')} />
      </div>

      {item.explanation ? (
        <section className="space-y-1 border-t pt-2">
          <h4 className="text-xs font-medium text-foreground">
            {t('anatel:consistency.whyFlagged')}
          </h4>
          <p className="text-xs text-muted-foreground">{item.explanation}</p>
        </section>
      ) : null}

      {item.shared_citations.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {item.shared_citations.map((citation) => (
            <li
              key={citation}
              className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {citation}
            </li>
          ))}
        </ul>
      ) : null}

      <footer className="border-t pt-2 text-[11px] text-muted-foreground">
        {t('anatel:consistency.producedBy', {
          model: item.model_name,
          prompt: item.prompt_version,
        })}
      </footer>
    </article>
  );
}

function PafSide({ side, label }: { side: PafContradictionSide; label: string }) {
  const { t, i18n } = useTranslation(['anatel']);
  const verified = side.evidence_verified === true;
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
        {/* Raw text, printed as stored. This corpus keeps the reference date as a
            string and this application does not convert it while reading. */}
        <span className="text-xs text-muted-foreground">{side.reference_date_raw ?? '—'}</span>
      </div>

      {side.case_key ? (
        <Link
          to={`/anatel/paf/c/${side.case_key}`}
          className="block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {side.case_number}
        </Link>
      ) : (
        <span className="text-sm text-foreground">{side.case_number ?? '—'}</span>
      )}

      <div className="flex flex-wrap gap-1">
        {side.outcome_codes.map((code) => (
          <span key={code} className="rounded border px-1.5 py-0.5 text-[11px] text-foreground">
            {t(`anatel:paf.outcomes.${code}`, { defaultValue: code })}
          </span>
        ))}
      </div>

      {side.credit_amount_brl !== null ? (
        // Labelled on the row, not only in the page caveat: this number sits
        // beside outcome codes and would otherwise read as a penalty.
        <p className="text-xs text-muted-foreground">
          {t('anatel:pafConsistency.creditOnRow', {
            amount: currency.format(side.credit_amount_brl),
          })}
        </p>
      ) : null}

      {side.proposition ? <p className="text-xs text-foreground">{side.proposition}</p> : null}

      {side.evidence ? (
        <blockquote
          className={cn(
            'border-l-2 pl-2 text-xs italic',
            verified ? 'border-muted text-muted-foreground' : 'border-destructive/50 text-destructive',
          )}
        >
          <span className="mb-1 flex items-center gap-1 not-italic">
            {verified ? (
              <ShieldCheck className="h-3 w-3" aria-hidden />
            ) : (
              <AlertTriangle className="h-3 w-3" aria-hidden />
            )}
            <span className="text-[11px]">
              {verified
                ? t('anatel:consistency.evidenceVerified')
                : t('anatel:consistency.evidenceUnverified')}
            </span>
          </span>
          {side.evidence}
        </blockquote>
      ) : null}
    </div>
  );
}
